import React, { useState, useEffect, useRef } from 'react';
import { Language } from '../types';
import { TRANSLATIONS } from '../translations';
import { Bot, Send, X, User, Search } from 'lucide-react';

interface AiAssistantProps {
  isOpen: boolean;
  onClose: () => void;
  kolName?: string; // Optional: if provided, context is about a specific KOL
  language: Language;
  onFilterKols?: (query: string) => void; // New prop for filtering
}

interface Message {
  id: string;
  sender: 'user' | 'ai';
  text: string;
  timestamp: Date;
  isAction?: boolean; // If true, this message represents an action taken
}

export const AiAssistant: React.FC<AiAssistantProps> = ({ isOpen, onClose, kolName, language, onFilterKols }) => {
  const t = TRANSLATIONS[language];
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Initial greeting depends on context
      const text = kolName 
        ? (language === 'en' 
            ? `Hello! I am the LinkKol AI Agent. I have analyzed ${kolName}'s on-chain data and social metrics. What would you like to know?` 
            : `你好！我是 LinkKol AI 助手。我已经分析了 ${kolName} 的链上数据和社交指标。你想了解什么？`)
        : (language === 'en'
            ? `Hello! I can help you find the perfect KOLs for your campaign. Try asking: "Find me Solana experts" or "Show me high engagement creators".`
            : `你好！我可以帮你筛选最适合的 KOL。你可以试着问："帮我找 Solana 专家" 或 "显示高互动率的创作者"。`);

      setMessages([
        {
          id: '1',
          sender: 'ai',
          text: text,
          timestamp: new Date()
        }
      ]);
    }
  }, [isOpen, kolName, language, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      sender: 'user',
      text: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    // AI Logic processing
    setTimeout(() => {
      const { responseText, action } = generateResponse(input, language, kolName);
      
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        sender: 'ai',
        text: responseText,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);

      // Trigger filter action if applicable and callback provided
      if (action && onFilterKols && !kolName) {
        onFilterKols(action);
      }

    }, 1500);
  };

  return (
    <div className={`fixed inset-y-0 right-0 w-96 bg-slate-900 border-l border-slate-700 shadow-2xl transform transition-transform duration-300 z-50 flex flex-col ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
      {/* Header */}
      <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-800">
        <div className="flex items-center gap-2">
          <div className="bg-indigo-600 p-1.5 rounded-lg">
             <Bot size={18} className="text-white" />
          </div>
          <div>
             <h3 className="text-sm font-bold text-white">{t.chatWithAi}</h3>
             <p className="text-xs text-slate-400">Powered by Gemini</p>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <X size={20} />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
              msg.sender === 'user' 
                ? 'bg-indigo-600 text-white rounded-tr-sm' 
                : 'bg-slate-800 text-slate-200 border border-slate-700 rounded-tl-sm'
            }`}>
              {msg.text}
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
             <div className="bg-slate-800 px-4 py-3 rounded-2xl rounded-tl-sm border border-slate-700 flex gap-1">
                <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce"></span>
                <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce delay-100"></span>
                <span className="w-2 h-2 bg-slate-500 rounded-full animate-bounce delay-200"></span>
             </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-slate-800 bg-slate-900">
        <div className="relative">
          <input 
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder={t.askAiPlaceholder}
            className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-4 pr-10 py-3 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
          <button 
            onClick={handleSend}
            disabled={!input.trim()}
            className="absolute right-2 top-2 p-1.5 bg-indigo-600 rounded-lg text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

// Response Logic
const generateResponse = (input: string, lang: Language, contextName?: string): { responseText: string, action?: string } => {
  const lowercaseInput = input.toLowerCase();

  // If we are in a specific KOL context
  if (contextName) {
     if (lang === 'en') {
      if (lowercaseInput.includes('risk') || lowercaseInput.includes('safe') || lowercaseInput.includes('dump')) {
        return { responseText: `Based on historical analysis, ${contextName} has a Brand Safety score of high confidence. There are no recorded "rug pulls" or "pump and dump" schemes associated with their wallet address.` };
      }
      if (lowercaseInput.includes('price') || lowercaseInput.includes('cost')) {
        return { responseText: `${contextName}'s estimated price range is consistent with market rates for their engagement level (approx $15 CPM).` };
      }
      return { responseText: `That's an interesting question about ${contextName}. My analysis suggests they are a strong candidate for this campaign.` };
    } else {
       if (lowercaseInput.includes('风险') || lowercaseInput.includes('安全')) {
        return { responseText: `基于历史数据分析，${contextName} 的品牌安全评分较高。未发现风险行为。` };
      }
      if (lowercaseInput.includes('价格') || lowercaseInput.includes('多少钱')) {
        return { responseText: `${contextName} 的预估报价符合当前互动率的市场行情（约 $15 CPM）。` };
      }
      return { responseText: `关于 ${contextName} 的分析表明，他们是本次推广的有力候选人。` };
    }
  }

  // General Search / Filter Context
  if (lang === 'en') {
    if (lowercaseInput.includes('solana') || lowercaseInput.includes('meme') || lowercaseInput.includes('degen')) {
      return { responseText: "I've filtered the list for Solana and Meme coin specialists. 'Meme King' seems like your best bet here.", action: 'meme solana' };
    }
    if (lowercaseInput.includes('nft') || lowercaseInput.includes('game')) {
       return { responseText: "Filtering for NFT and Gaming KOLs. 'NFT Hunter' has excellent conversion rates for mints.", action: 'nft game' };
    }
    if (lowercaseInput.includes('defi') || lowercaseInput.includes('tech')) {
       return { responseText: "Showing technical DeFi analysts. 'Crypto Wizard' is highly recommended for infrastructure projects.", action: 'defi tech' };
    }
    return { responseText: "I can help you filter KOLs. Try keywords like 'DeFi', 'NFT', 'Solana', or 'High Trust'.", action: '' };
  } else {
     if (lowercaseInput.includes('solana') || lowercaseInput.includes('土狗') || lowercaseInput.includes('meme')) {
      return { responseText: "已为您筛选 Solana 和 Meme 领域的专家。'Meme King' 看起来最适合。", action: 'meme solana' };
    }
    if (lowercaseInput.includes('nft') || lowercaseInput.includes('游戏') || lowercaseInput.includes('game')) {
       return { responseText: "已为您筛选 NFT 和链游 KOL。'NFT Hunter' 的 Mint 转化率非常高。", action: 'nft game' };
    }
    if (lowercaseInput.includes('defi') || lowercaseInput.includes('技术')) {
       return { responseText: "正在展示硬核 DeFi 分析师。推荐 'Crypto Wizard' 进行基础设施类推广。", action: 'defi tech' };
    }
    return { responseText: "我可以帮您筛选 KOL。试试输入 'DeFi', 'NFT', 'Solana' 或 '高信用'。", action: '' };
  }
};